import { useMemo, useRef, useState } from "react"
import { HugeiconsIcon } from "@hugeicons/react"
import {
  ArrowLeft01Icon,
  DeliveryBox01Icon,
  PackageIcon,
  Search01Icon,
} from "@hugeicons/core-free-icons"
import { useQuery } from "@tanstack/react-query"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Input } from "@/components/ui/input"
import { useDebounce } from "@/lib/hooks/use-debounce"
import { cn } from "@/lib/utils"
import {
  listOrderLineItemReferences,
  searchOrderReferences,
} from "../api/assistant-api"
import type {
  ChatReference,
  LineItemReferenceResult,
  OrderReferenceResult,
} from "../types/assistant.types"

interface ReferencePickerPopoverProps {
  /** Trigger element. Pass a Button or icon button. */
  trigger: React.ReactNode
  /** Called when the user picks an order or line-item reference. */
  onSelect: (reference: ChatReference) => void
  /** When set, the picker opens directly on the line-item tab for this
   *  order — used for "Add a line item" actions inside a chat that
   *  already has an order in scope. */
  defaultOrderId?: number | null
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

type PickerView =
  | { mode: "orders"; query: string }
  | { mode: "line-items"; orderId: number; orderLabel: string }

/** "@"-mention picker for orders + line items. Two-step flow: search
 *  orders, click one to drill into its line items, click an item to
 *  return a structured ``ChatReference``. The composer wraps it as a
 *  pending chip and ships it with the next chat turn. */
export function ReferencePickerPopover({
  trigger,
  onSelect,
  defaultOrderId,
  open: openProp,
  onOpenChange,
}: ReferencePickerPopoverProps) {
  const [internalOpen, setInternalOpen] = useState(false)
  const open = openProp ?? internalOpen
  const [view, setView] = useState<PickerView>({ mode: "orders", query: "" })

  const setOpen = (v: boolean) => {
    if (v) {
      // Opening — reset to a sensible default so a second @-mention
      // doesn't carry stale state from the previous one. Doing it
      // here (in the handler) keeps the open state in sync with the
      // view state without an effect.
      if (defaultOrderId) {
        setView({
          mode: "line-items",
          orderId: defaultOrderId,
          orderLabel: `Order #${defaultOrderId}`,
        })
      } else {
        setView({ mode: "orders", query: "" })
      }
      // Focus the search input on next tick so the popover content
      // has mounted.
      setTimeout(() => inputRef.current?.focus(), 0)
    }
    onOpenChange?.(v)
    setInternalOpen(v)
  }

  const debouncedQuery = useDebounce(
    view.mode === "orders" ? view.query : "",
    200,
  )

  const ordersQuery = useQuery({
    queryKey: ["reference-orders", debouncedQuery],
    queryFn: () => searchOrderReferences(debouncedQuery),
    enabled: open && view.mode === "orders",
  })

  const lineItemsQuery = useQuery({
    queryKey: ["reference-line-items", view.mode === "line-items" ? view.orderId : null],
    queryFn: () =>
      listOrderLineItemReferences((view as { orderId: number }).orderId),
    enabled: open && view.mode === "line-items",
  })

  const inputRef = useRef<HTMLInputElement>(null)

  function selectOrder(o: OrderReferenceResult) {
    setView({ mode: "line-items", orderId: o.id, orderLabel: o.label })
  }

  function pickOrder(o: OrderReferenceResult) {
    onSelect({ type: "order", id: o.id, label: o.label })
    setOpen(false)
  }

  function pickLineItem(li: LineItemReferenceResult) {
    onSelect({
      type: "line_item",
      id: li.id,
      order_id: li.order_id,
      label: li.label,
    })
    setOpen(false)
  }

  const orders = ordersQuery.data ?? []
  const lineItems = lineItemsQuery.data ?? []

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {/* ``inline-flex`` (not ``contents``) gives the trigger a real
          layout box so the base-ui positioner can anchor the popup
          next to it. With ``display: contents`` the trigger has no
          bounding rect and the popup falls back to the viewport
          origin (top-left of the screen). */}
      <PopoverTrigger className="inline-flex">{trigger}</PopoverTrigger>
      <PopoverContent side="top" align="start" sideOffset={8} className="w-80 p-0">
        {view.mode === "orders" ? (
          <OrdersPane
            inputRef={inputRef}
            query={view.query}
            onQueryChange={(q) => setView({ mode: "orders", query: q })}
            results={orders}
            isLoading={ordersQuery.isLoading}
            onSelectOrder={selectOrder}
            onPickOrder={pickOrder}
          />
        ) : (
          <LineItemsPane
            orderLabel={view.orderLabel}
            results={lineItems}
            isLoading={lineItemsQuery.isLoading}
            onBack={() => setView({ mode: "orders", query: "" })}
            onPick={pickLineItem}
          />
        )}
      </PopoverContent>
    </Popover>
  )
}

interface OrdersPaneProps {
  inputRef: React.RefObject<HTMLInputElement | null>
  query: string
  onQueryChange: (q: string) => void
  results: OrderReferenceResult[]
  isLoading: boolean
  onSelectOrder: (o: OrderReferenceResult) => void
  onPickOrder: (o: OrderReferenceResult) => void
}

function OrdersPane({
  inputRef,
  query,
  onQueryChange,
  results,
  isLoading,
  onSelectOrder,
  onPickOrder,
}: OrdersPaneProps) {
  const empty = !isLoading && results.length === 0
  return (
    <div className="flex flex-col">
      <div className="relative border-b p-2">
        <HugeiconsIcon
          icon={Search01Icon}
          size={12}
          className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground"
        />
        <Input
          ref={inputRef}
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder="Search orders…"
          className="pl-7"
        />
      </div>
      <ul
        className={cn(
          "max-h-72 overflow-y-auto py-1",
          empty && "p-3 text-center text-xs text-muted-foreground",
        )}
      >
        {isLoading && (
          <li className="px-3 py-2 text-xs text-muted-foreground">
            Searching…
          </li>
        )}
        {empty && <li>No orders match.</li>}
        {results.map((o) => (
          // Two siblings (pick + drill-into-lines) share a hover-revealed
          // group. The outer element is a non-interactive ``div`` — a
          // <button>-inside-<button> would be invalid HTML and trip
          // hydration warnings + a11y issues.
          <li key={o.id} className="group flex items-stretch gap-1 px-1">
            <button
              type="button"
              onClick={() => onPickOrder(o)}
              className="flex flex-1 items-start gap-2 rounded-md px-2 py-2 text-left hover:bg-muted"
            >
              <HugeiconsIcon
                icon={DeliveryBox01Icon}
                size={14}
                className="mt-0.5 shrink-0 text-muted-foreground"
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 text-sm">
                  <span className="font-medium">{o.label}</span>
                  {o.status && (
                    <span className="text-[10px] uppercase text-muted-foreground">
                      {o.status}
                    </span>
                  )}
                </div>
                {(o.name || o.customer) && (
                  <div className="truncate text-xs text-muted-foreground">
                    {[o.name, o.customer].filter(Boolean).join(" · ")}
                  </div>
                )}
              </div>
            </button>
            <button
              type="button"
              onClick={() => onSelectOrder(o)}
              className="shrink-0 self-center rounded px-1.5 py-0.5 text-[10px] text-muted-foreground opacity-0 ring-1 ring-border transition-opacity hover:bg-background group-hover:opacity-100 focus:opacity-100"
              title={`Browse line items in ${o.label}`}
            >
              Lines →
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}

interface LineItemsPaneProps {
  orderLabel: string
  results: LineItemReferenceResult[]
  isLoading: boolean
  onBack: () => void
  onPick: (li: LineItemReferenceResult) => void
}

function LineItemsPane({
  orderLabel,
  results,
  isLoading,
  onBack,
  onPick,
}: LineItemsPaneProps) {
  const [filter, setFilter] = useState("")
  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase()
    if (!q) return results
    return results.filter(
      (li) =>
        li.label.toLowerCase().includes(q) ||
        li.code.toLowerCase().includes(q) ||
        li.description.toLowerCase().includes(q),
    )
  }, [filter, results])
  const empty = !isLoading && filtered.length === 0

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-1 border-b p-2">
        <button
          type="button"
          onClick={onBack}
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
          title="Back to orders"
        >
          <HugeiconsIcon icon={ArrowLeft01Icon} size={12} />
        </button>
        <div className="text-xs font-medium">{orderLabel}</div>
        <Input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter…"
          className="ml-1 h-6"
        />
      </div>
      <ul
        className={cn(
          "max-h-72 overflow-y-auto py-1",
          empty && "p-3 text-center text-xs text-muted-foreground",
        )}
      >
        {isLoading && (
          <li className="px-3 py-2 text-xs text-muted-foreground">
            Loading line items…
          </li>
        )}
        {empty && <li>No line items match.</li>}
        {filtered.map((li) => (
          <li key={li.id}>
            <button
              type="button"
              onClick={() => onPick(li)}
              className="flex w-full items-start gap-2 rounded-md px-3 py-2 text-left hover:bg-muted"
            >
              <HugeiconsIcon
                icon={PackageIcon}
                size={14}
                className="mt-0.5 shrink-0 text-muted-foreground"
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 text-sm">
                  <span className="font-medium">{li.label}</span>
                  {li.qty > 0 && (
                    <span className="text-[10px] text-muted-foreground">
                      qty {li.qty}
                    </span>
                  )}
                </div>
                {(li.dimensions || li.description) && (
                  <div className="truncate text-xs text-muted-foreground">
                    {[li.dimensions, li.description].filter(Boolean).join(" · ")}
                  </div>
                )}
              </div>
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}
